import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { useDataSource } from "@/adapters/data-source";
import { hsnListQuery } from "@/modules/catalog/hsnQuery";
import { projectHsnCode } from "@/modules/catalog/projections";
import { LoadFailed, NoMatches } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { renderCell } from "@/components/table/cells";
import { RecordDrawer } from "@/components/shell/RecordDrawer";
import { DataTable } from "@/components/table/DataTable";
import { Pagination } from "@/components/table/Pagination";
import { useCursorHistory } from "@/components/table/useCursorHistory";
import { HsnCodeDetail } from "@/modules/catalog/screens/HsnCodeDetail";
import type { SectionScreenProps } from "@/modules/types";
import { useOpenRecord } from "@/routes/useOpenRecord";

/**
 * The HSN master (FR-CAT-10).
 *
 * Read-only here. Codes arrive from Shopify with the variants that carry
 * them, and rates are edited in GST Settings. A row opens the products using
 * that code.
 */
export function HsnMaster({
  fields,
  filter,
  sort,
  onToggleSort,
  onClearFilter,
}: SectionScreenProps) {
  const data = useDataSource();

  const [limit, setLimit] = useState(25);
  const [history, setHistory] = useCursorHistory();
  const cursor = history[history.length - 1];
  const { recordId, open } = useOpenRecord();

  const query = useQuery({
    queryKey: ["catalog", "hsn", "list", filter, sort, limit, cursor],
    queryFn: () =>
      data.catalog.listHsnCodes({
        ...hsnListQuery({ filter, sort }),
        limit,
        cursor,
      }),
  });

  if (query.isPending) return <TableSkeleton />;

  if (query.isError) {
    return (
      <LoadFailed
        message={query.error.message}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {recordId !== null ? (
        <RecordDrawer
          title={
            query.data.items.find((code) => code.id === recordId)?.code ??
            "HSN code"
          }
        >
          {(() => {
            const hsn = query.data.items.find((code) => code.id === recordId);
            return hsn ? (
              <HsnCodeDetail hsn={hsn} />
            ) : (
              <p className="text-muted-foreground p-4 text-sm">
                This code is not on the current page.
              </p>
            );
          })()}
        </RecordDrawer>
      ) : null}
      <DataTable
        fields={fields}
        rows={query.data.items}
        getRowId={(code) => code.id}
        project={projectHsnCode}
        sort={sort}
        onToggleSort={onToggleSort}
        onRowClick={(row) => {
          open(row.id);
        }}
        renderOverride={(field, row) => {
          // Rates are percentages, not money — `number` is the closest
          // shared field type and it renders with no unit. A bare "18"
          // reads as a quantity; "18%" is the one character that says what
          // it actually is.
          if (field.name === "gstRate") {
            return <>{renderCell(field, row.gstRate)}%</>;
          }
          if (field.name === "productCount") {
            const count = row.productCount ?? 0;
            return (
              <span className="tabular-nums">
                {count} product{count === 1 ? "" : "s"}
              </span>
            );
          }
          if (field.name === "cessRate") {
            return <>{renderCell(field, row.cessRate)}%</>;
          }
          return undefined;
        }}
        emptyState={<NoMatches onClear={onClearFilter} />}
        footer={
          <Pagination
            page={query.data}
            limit={limit}
            onLimitChange={(next) => {
              setLimit(next);
              setHistory([undefined]);
            }}
            history={history}
            onNavigate={setHistory}
          />
        }
      />
    </div>
  );
}
